import React from "react";
import useTranslation from "../../../../../hooks/i18n/useTranslation";

import IconsEnum from "../../../../../classes/enums/IconsEnum";
import { roundToUnit } from "../../../../../classes/utils/NumbersUtils";
import IconButton from "../../../../../components/iconButton/IconButton";
import WidgetBox from "../../../../../components/widgetBox/WidgetBox";

import { ButtonSize } from "../../../../../classes/enums/ButtonEnum";
import "../History.css";

// Format value with degree symbol
const formatValue = value => {
  if (value === null || value === undefined) {
    return "-";
  }
  return `${roundToUnit(value)}°`;
};

const GlobalAlignmentHistory = ({
  currentGlobalAlignment,
  onDeleteGlobalAlignment,
  onEditGlobalAlignment,
  comparedGlobalAlignment = null,
  compareMode = false,
  currentPlanLabel = "Plan 1",
  comparedPlanLabel = "Plan 2",
}) => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = React.useState(false);

  const hasAnyAlignment = currentGlobalAlignment || comparedGlobalAlignment;

  // If no alignment exists, don't render the widget
  if (!hasAnyAlignment) {
    return null;
  }

  const handleEditGlobalAlignment = () => {
    if (onEditGlobalAlignment) {
      onEditGlobalAlignment(currentGlobalAlignment);
    }
  };

  // Pre values (original)
  const prePelvicTilt = currentGlobalAlignment?.originalPelvicTilt;
  const preKneeAngle = currentGlobalAlignment?.originalKneeAngle;

  // Plan values (current)
  const planPelvicTilt = currentGlobalAlignment?.pelvicTilt;
  const planKneeAngle = currentGlobalAlignment?.kneeAngle;

  const preValues = {
    pelvicTilt:
      currentGlobalAlignment?.originalPelvicTilt ??
      comparedGlobalAlignment?.originalPelvicTilt,
    kneeAngle:
      currentGlobalAlignment?.originalKneeAngle ??
      comparedGlobalAlignment?.originalKneeAngle,
  };

  const compareRows = [
    {
      label: "Pelvic Tilt",
      pre: preValues.pelvicTilt,
      current: currentGlobalAlignment?.pelvicTilt,
      compared: comparedGlobalAlignment?.pelvicTilt,
    },
    {
      label: "Knee Angle",
      pre: preValues.kneeAngle,
      current: currentGlobalAlignment?.kneeAngle,
      compared: comparedGlobalAlignment?.kneeAngle,
    },
  ];

  if (compareMode) {
    return (
      <WidgetBox
        title={"Decompensation"}
        isSmall={true}
        isInSideBar={true}
        isExpandable={true}
      >
        <div className="side-widget-history">
          <div className="global-alignment-table">
            <div className="global-alignment-header-row compare-mode">
              <div className="global-alignment-label"></div>
              <div className="global-alignment-column-header App-text-medium">
                Pre
              </div>
              <div className="global-alignment-column-header App-text-medium">
                {currentPlanLabel}
              </div>
              <div className="global-alignment-column-header App-text-medium">
                {comparedPlanLabel}
              </div>
              <div className="global-alignment-edit"></div>
            </div>

            {compareRows.map(row => {
              const isUnavailable =
                row.pre == null && row.current == null && row.compared == null;
              return (
              <div
                className={`global-alignment-data-row compare-mode${isUnavailable ? " global-alignment-data-row--unavailable" : ""}`}
                key={row.label}
              >
                <div className="global-alignment-label App-text-medium">
                  {row.label}
                </div>
                <div className="global-alignment-value App-text-medium">
                  {formatValue(row.pre)}
                </div>
                <div className="global-alignment-value App-text-medium">
                  {formatValue(row.current)}
                </div>
                <div className="global-alignment-value App-text-medium">
                  {formatValue(row.compared)}
                </div>
                <div className="global-alignment-spacer"></div>
              </div>
              );
            })}
          </div>
        </div>
      </WidgetBox>
    );
  }

  return (
    <WidgetBox
      title={"Decompensation"}
      isSmall={true}
      isInSideBar={true}
      isExpandable={true}
    >
      <div className="side-widget-history">
        {/* Column Headers with Edit Button */}
        <div className="global-alignment-table">
          <div className="global-alignment-header-row">
            <div className="global-alignment-label"></div>
            <div className="global-alignment-column-header App-text-medium">
              Pre
            </div>
            <div className="global-alignment-column-header App-text-medium">
              Plan
            </div>
            <div className="global-alignment-edit">
              <IconButton
                icon={IconsEnum.EDIT}
                size={ButtonSize.SMALL}
                isCircle={true}
                noBgColor
                onClick={handleEditGlobalAlignment}
                disabled={isLoading}
                title={t("208")}
              />
            </div>
          </div>

          {/* Pelvic Tilt Row */}
          <div className="global-alignment-data-row">
            <div className="global-alignment-label App-text-medium">
              Pelvic Tilt
            </div>
            <div className="global-alignment-value App-text-medium">
              {formatValue(prePelvicTilt)}
            </div>
            <div className="global-alignment-value App-text-medium">
              {formatValue(planPelvicTilt)}
            </div>
            <div className="global-alignment-spacer"></div>
          </div>

          {/* Knee Angle Row */}
          <div className={`global-alignment-data-row${preKneeAngle == null && planKneeAngle == null ? " global-alignment-data-row--unavailable" : ""}`}>
            <div className="global-alignment-label App-text-medium">
              Knee Angle
            </div>
            <div className="global-alignment-value App-text-medium">
              {formatValue(preKneeAngle)}
            </div>
            <div className="global-alignment-value App-text-medium">
              {formatValue(planKneeAngle)}
            </div>
            <div className="global-alignment-spacer"></div>
          </div>
        </div>
      </div>
    </WidgetBox>
  );
};

export default GlobalAlignmentHistory;
